import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    http.get("/notifications")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const unreadCount = items.filter((i) => !i.read).length;

  const markRead = async (id) => {
    try {
      await http.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, read: true } : i));
    } catch {}
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    setMarkingAll(true);
    try {
      await http.post("/notifications/read-all");
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[var(--brand-primary)]">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-[var(--text-secondary)] mt-0.5">{unreadCount} unread</p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            disabled={markingAll}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--border-default)] text-sm font-medium text-[var(--text-secondary)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors disabled:opacity-50"
          >
            {markingAll ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
            Mark all read
          </button>
        )}
      </div>

      {loading ? (
        <div className="card-surface p-8 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-[var(--brand-accent)]" />
        </div>
      ) : items.length === 0 ? (
        <div className="card-surface p-8 text-center">
          <Bell size={28} className="text-[var(--border-default)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-secondary)]">You're all caught up — no notifications yet.</p>
        </div>
      ) : (
        items.map((n) => (
          <div
            key={n.id}
            onClick={() => !n.read && markRead(n.id)}
            className={`card-surface p-4 flex items-start gap-3 transition-colors ${
              n.read ? "opacity-60" : "cursor-pointer hover:border-[var(--brand-accent)]"
            }`}
            data-testid={`notif-${n.id}`}
          >
            <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 mt-0.5 ${n.read ? "bg-[var(--bg-subtle)]" : "bg-[var(--brand-accent)]/15"}`}>
              <Bell size={14} className={n.read ? "text-[var(--text-tertiary)]" : "text-[var(--brand-accent)]"} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${n.read ? "text-[var(--text-secondary)]" : "text-[var(--brand-primary)]"}`}>{n.title}</div>
              {n.body && <div className="text-sm text-[var(--text-secondary)] mt-0.5 leading-relaxed">{n.body}</div>}
              <div className="text-xs text-[var(--text-tertiary)] mt-1.5">{new Date(n.created_at).toLocaleString()}</div>
            </div>
            {!n.read && <div className="w-2 h-2 rounded-full bg-[var(--brand-accent)] shrink-0 mt-2" />}
          </div>
        ))
      )}
    </div>
  );
}
